import React from "react";
import styled from "@emotion/styled";
import { Link } from "gatsby";
import { breakpoints, theme } from "../../utils/styles";
import { Global } from "@emotion/core";
import { Helmet } from "react-helmet";
import SynbiTECHImage from "../../assets/Synbitech_speaker announcement.jpg";

const Page = styled.main({
  minHeight: '100vh',
  background: theme.white,
  color: theme.black,
  margin: 0,
  padding: 0,
  position: 'relative',
});

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 120px 32px 64px;
  
  @media (max-width: ${breakpoints.l}px) {
    padding: 100px 24px 48px;
  }
`;

const Header = styled.header`
  margin-bottom: 48px;
  text-align: left;
`;

const Title = styled.h1`
  font-size: clamp(28px, 3.5vw, 40px);
  line-height: 1.2;
  margin: 0 0 16px 0;
  color: ${theme.black};
  font-family: Nunito, sans-serif;
  font-weight: 700;
`;

const Meta = styled.div`
  font-size: 1rem;
  color: rgba(0,0,0,0.6);
  font-family: Nunito, sans-serif;
  margin-bottom: 32px;
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: contain;
  border-radius: 8px;
  margin-bottom: 48px;
  background: #f5f5f5;
  
  @media (max-width: ${breakpoints.l}px) {
    height: 300px;
    margin-bottom: 32px;
  }
`;

const Content = styled.div`
  font-family: Nunito, sans-serif;
  line-height: 1.7;
  font-size: 1.1rem;
  color: ${theme.black};
  
  p {
    margin: 0 0 24px 0;
  }
  
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 48px 0 24px 0;
    color: ${theme.black};
  }
  
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    margin: 32px 0 16px 0;
    color: ${theme.black};
  }
  
  blockquote {
    border-left: 4px solid ${theme.primary};
    margin: 32px 0;
    padding: 16px 24px;
    background: #f9f9f9;
    font-style: italic;
  }
  
  ul, ol {
    margin: 24px 0;
    padding-left: 24px;
  }
  
  li {
    margin-bottom: 8px;
  }
  
  .highlight {
    background: linear-gradient(120deg, ${theme.primary}20 0%, ${theme.primary}20 100%);
    padding: 2px 4px;
    border-radius: 3px;
  }
  
  a {
    color: ${theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${theme.primary};
  text-decoration: none;
  font-family: Nunito, sans-serif;
  font-weight: 600;
  margin-bottom: 32px;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:before {
    content: "←";
    font-size: 1.2rem;
  }
`;

export default function SynbiTECH2025Post() {
  return (
    <Page>
      <Helmet>
        <title>Scindo CEO Gustaf Hemberg to Speak at SynbiTECH 2025 | Scindo</title>
        <meta name="description" content="Gustaf Hemberg, CEO and Co-founder of Scindo, will speak at SynbiTECH 2025 in London this December, presenting on AI-guided enzyme design for clean chemistry." />
        
        {/* Open Graph tags for social media sharing */}
        <meta property="og:title" content="Scindo CEO Gustaf Hemberg to Speak at SynbiTECH 2025" />
        <meta property="og:description" content="Gustaf Hemberg will present Scindo's work on enabling clean chemistry through AI-guided enzyme design at SynbiTECH 2025 in London." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.scindo.bio/blog/synbitech-2025-speaker" />
        <meta property="og:site_name" content="Scindo" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Scindo CEO Gustaf Hemberg to Speak at SynbiTECH 2025" />
        <meta name="twitter:description" content="Gustaf Hemberg will present at SynbiTECH 2025 in London this December." />
        
        {/* Additional SEO tags */}
        <meta name="author" content="Scindo" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.scindo.bio/blog/synbitech-2025-speaker" />
      </Helmet>
      
      <Global styles={{
        'html, body': {
          background: theme.white,
          color: theme.black,
          margin: 0,
          padding: 0,
        },
        '*': {
          boxSizing: 'border-box',
        },
      }} />
      
      <Container>
        <BackLink to="/media">Back to Media</BackLink>
        
        <Header>
          <Title>Scindo CEO Gustaf Hemberg to Speak at SynbiTECH 2025</Title>
          <Meta>November 2025</Meta>
        </Header>
        
        <FeaturedImage 
          src={SynbiTECHImage} 
          alt="Gustaf Hemberg speaking at SynbiTECH 2025"
        />
        
        <Content>
          <p>
            We're delighted to announce that <span className="highlight">Gustaf Hemberg, CEO and Co-founder of Scindo</span>, will be speaking at SynbiTECH 2025, taking place this December in London.
          </p>
          
          <p>
            SynbiTECH is Europe's leading conference dedicated to the business and science of synthetic biology, bringing together innovators, investors, and industry leaders who are shaping the future of sustainable biomanufacturing.
          </p>
          
          <p>
            Gustaf will join the Company Reviews session on Day 1, where he will present Scindo's work on enabling clean chemistry through AI-guided enzyme design. His talk will explore how Scindo is transforming renewable and upcycled feedstocks into high-value chemicals — creating cleaner, smarter alternatives to traditional petrochemical manufacturing.
          </p>
          
          <blockquote>
            "At Scindo, we're designing enzymes to unlock new routes to sustainability within the chemical industry, SynbiTECH is an great platform to connect with others who share that vision — combining biology and machine learning to build a more circular economy."
          </blockquote>
          
          <p>
            Scindo's platform leverages machine learning and enzyme engineering to create efficient, scalable biocatalytic processes for producing ingredients used across flavour & fragrance, cosmetics, nutraceutical, and pharma markets. By redesigning chemistry from the enzyme up, Scindo aims to help manufacturers decarbonise production and replace fossil-derived inputs with renewable ones.
          </p>
          
          <p>
            We look forward to joining the SynbiTECH community to share insights, exchange ideas, and discuss how AI and biology together can drive a new era of sustainable chemistry.
          </p>
          
          <p>
            🔗 <a href="https://www.linkedin.com/feed/update/urn:li:activity:7366489325234040832/" target="_blank" rel="noopener noreferrer">Read the official speaker announcement from SynbiCITE on LinkedIn</a>
          </p>
        </Content>
      </Container>
    </Page>
  );
}
